import { useMemo, memo, useCallback } from 'react';
import {
  AG2,
  AR2,
  Table,
  Grid,
  LoginAvatar,
  SecurityScoreIcon,
  useTranslations,
  IUseTableReturn,
  IUseTableFetchReturn,
  IUseTableLocalizationReturn,
  TypographyWithTooltip,
  IMaterialTableOptions,
  IMaterialTableProps,
  getTimeFromNow,
} from '@uniqkey-frontend/shared-app';
import { GetVaultPasswordsResponseModel } from '@uniqkey-backend-organization-web/api-client';
import { useNavigate } from 'react-router-dom';
import { IEmployeeGroupLoginsTableRow } from '../../../hooks/tables/useEmployeeGroupLoginsTable';
import { MFA_TRANSLATION_KEYS } from '../../../constants';
import PageRouteEnum from '../../../enums/PageRouteEnum';

interface IGroupLoginsTableProps extends
  Omit<IUseTableReturn<IEmployeeGroupLoginsTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Omit<IUseTableFetchReturn<GetVaultPasswordsResponseModel>, 'data' | 'refetch'>
{
  logins: IUseTableFetchReturn<GetVaultPasswordsResponseModel>['data'];
  selectedLogins: IUseTableReturn<IEmployeeGroupLoginsTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
}

const TABLE_OPTIONS: IMaterialTableOptions<IEmployeeGroupLoginsTableRow> = {
  selection: true,
};

const MFA_COLORS = {
  true: AG2,
  false: AR2,
};

const TOOLTIP_PROPS = { cursorPointer: true };

const GroupLoginsTable = (props: IGroupLoginsTableProps) => {
  const {
    logins: preLogins,
    isLoading,
    total,
    localization,
    activePage,
    setActivePage,
    perPage,
    onPerPageChange,
    columnOrderBy,
    onColumnOrderByChange,
    columnOrderDirection,
    onColumnOrderDirectionChange,
    selectedLogins,
    onRowSelectionChange,
    createColumns,
  } = props;

  const { t, currentLanguage } = useTranslations();
  const navigate = useNavigate();

  const logins = useMemo(() => preLogins.map((login) => ({
    ...login,
    lastActivity: getTimeFromNow({
      date: login.activityAt,
      locale: currentLanguage,
    }),
  })), [preLogins, currentLanguage]);

  const columns = useMemo(() => createColumns([
    {
      title: t('loginsTab.table.columns.loginName'),
      field: 'name',
      render: (rowData) => (
        <Grid container flexWrap="nowrap" alignItems="center">
          <LoginAvatar src={rowData.iconUrl} />
          <TypographyWithTooltip variant="body2">
            {rowData.name}
          </TypographyWithTooltip>
        </Grid>
      ),
      width: '30%',
    },
    {
      title: t('loginsTab.table.columns.userName'),
      field: 'username',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.username}
        </TypographyWithTooltip>
      ),
      width: '25%',
    },
    {
      title: t('loginsTab.table.columns.securityScore'),
      field: 'strength',
      render: (rowData) => (
        <SecurityScoreIcon percentage={rowData.strength} />
      ),
      width: '15%',
    },
    {
      title: (
        <TypographyWithTooltip
          variant="subtitle1"
          tooltipProps={TOOLTIP_PROPS}
          lineClamp={5}
          title={t('loginsTab.table.columns.mfa.columnTooltip')}
        >
          {t('loginsTab.table.columns.mfa')}
        </TypographyWithTooltip>
      ),
      field: 'hasTotp',
      render: (rowData) => {
        const is2FAEnabledAsString = rowData.hasTotp.toString();
        return (
          <TypographyWithTooltip
            variant="body2"
            color={MFA_COLORS[is2FAEnabledAsString as keyof typeof MFA_COLORS]}
          >
            {t(MFA_TRANSLATION_KEYS[is2FAEnabledAsString as keyof typeof MFA_TRANSLATION_KEYS])}
          </TypographyWithTooltip>
        );
      },
      width: '15%',
    },
    {
      title: (
        <TypographyWithTooltip
          variant="subtitle1"
          tooltipProps={TOOLTIP_PROPS}
          lineClamp={5}
          title={t('loginsTab.table.columns.lastActivity.columnTooltip')}
        >
          {t('loginsTab.table.columns.lastActivity')}
        </TypographyWithTooltip>
      ),
      field: 'activityAt',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.lastActivity}
        </TypographyWithTooltip>
      ),
      width: '15%',
    },
  ]), [createColumns, t]);

  const handleLoginRowClick = useCallback<
    NonNullable<IMaterialTableProps<IEmployeeGroupLoginsTableRow>['onRowClick']>
  >((event, login) => {
    navigate(`${PageRouteEnum.AllSecuredData}/logins/${login!.vaultId}`);
  }, [navigate]);

  return (
    <Table
      t={t}
      totalItems={total}
      columns={columns}
      data={logins}
      isLoading={isLoading}
      options={TABLE_OPTIONS}
      activePage={activePage}
      onActivePageChange={setActivePage}
      perPage={perPage}
      onPerPageChange={onPerPageChange}
      columnOrderBy={columnOrderBy}
      onColumnOrderByChange={onColumnOrderByChange}
      columnOrderDirection={columnOrderDirection}
      onColumnOrderDirectionChange={onColumnOrderDirectionChange}
      selectedRows={selectedLogins}
      onSelectionChange={onRowSelectionChange}
      localization={localization}
      onRowClick={handleLoginRowClick}
      rowSelectByKey="vaultId"
      rowHeight={51}
    />
  );
};

export default memo(GroupLoginsTable);
